import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useAuth, useCurrentUser } from '@/auth/context'
import { relativeTime } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Danger zone — S-D3's last settings panel, on S-B12's endpoints.
 *
 * S-B8 left this one out and said why: DPDP export and organisation deletion
 * are launch requirements S-B12 owns, and "pause all automation" had no
 * automation to pause. **All three are built now (S-G2 closed the third, audit
 * row G29)**, and the switch behind it is real: one boolean per organisation
 * that the two run paths and the three LinkedIn gates all read.
 *
 * **The pause is one press and no modal, unlike the deletion below it.** The
 * modal exists to stop an irreversible accident; this is reversible by the same
 * button, it destroys nothing, and the cancellation screen sends people here
 * *offering* it — putting a confirmation in front of the thing being offered as
 * the gentle alternative to leaving is a dark pattern in the other direction.
 *
 * **Every member sees the state; owners and admins move it.** `budget:manage`,
 * which the server draws the same line for, on S-D3's *Change budget caps* row.
 * A member who cannot see that automation is off has no answer to "why has
 * nothing published this week", and a product that looks broken is worse than
 * one that says it is paused.
 *
 * **The export is asynchronous and the screen says so.** S-D3's own copy is
 * "emailed within 24 hours", and the API answers `202`: building the archive
 * streams every table in the organisation's schema. What this panel shows is
 * the queue and the history, so "did my request go through" has an answer that
 * is not an email that has not arrived yet.
 *
 * **The delete confirmation is checked on the server, not here.** Typing the
 * name is what the modal asks for and the button stays disabled until it
 * matches — but the API refuses a mismatch with `CONFIRMATION_REQUIRED`
 * regardless, because a confirmation enforced only in the browser is a
 * confirmation an accidental `curl` skips. The two are not redundant: one
 * prevents the accident, the other prevents the mistake.
 */
export function DangerZonePanel(): JSX.Element {
  const { tenant, role } = useActiveTenant()
  const { user } = useCurrentUser()
  const { signOut } = useAuth()
  const toast = useToast()
  const [confirmation, setConfirmation] = useState('')
  const [deleting, setDeleting] = useState(false)
  const now = new Date()
  const mayPause = may(role, 'budget:manage')

  const exports = useQuery((signal) => call(() => api.GET('/api/v1/exports', { signal })), [])
  const retention = useQuery((signal) => call(() => api.GET('/api/v1/retention', { signal })), [])
  const automation = useQuery((signal) => call(() => api.GET('/api/v1/automation', { signal })), [])

  const request = useMutation(() => call(() => api.POST('/api/v1/exports', {})))
  const download = useMutation((exportId: number) =>
    call(() =>
      api.POST('/api/v1/exports/{export_id}/download', {
        params: { path: { export_id: exportId } },
      }),
    ),
  )
  // One mutation for both directions: the two routes differ only in their path,
  // and a pair of hooks would give the row two independent `pending` flags for
  // one button that is only ever in one of them.
  const move = useMutation((next: boolean) =>
    call(() =>
      next ? api.POST('/api/v1/automation/pause', {}) : api.POST('/api/v1/automation/resume', {}),
    ),
  )
  const erase = useMutation((typed: string) =>
    call(() =>
      api.POST('/api/v1/organisation/deletion', { body: { confirmation: typed, reason: null } }),
    ),
  )

  const rows = exports.state.data?.exports ?? []
  const inFlight = rows.some((row) => row.state === 'pending' || row.state === 'running')
  const keptDays = retention.state.data?.export_retained_days ?? null
  const switched = automation.state.data ?? null
  const paused = switched?.paused ?? false

  return (
    <>
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Data &amp; automation</h3>
        </div>
        <div className="ar-card__body ar-stack">
          <div className="app-danger__row">
            <div>
              <strong>Pause all automation</strong>
              <p className="ar-muted app-danger__note">
                Stops audits, content, and outreach. Your data and rankings stay. Reversible.
              </p>
              {automation.state.status === 'loading' ? (
                <div className="app-danger__caption">
                  <Loading label="Checking the automation switch" lines={1} />
                </div>
              ) : null}
              {automation.state.status === 'error' ? (
                <ErrorState
                  failure={automation.state.failure}
                  title="Could not tell whether automation is running"
                  onRetry={automation.refetch}
                />
              ) : null}
              {switched !== null ? (
                <p className="ar-subtle app-danger__caption">
                  {paused
                    ? /* The one thing this line must never do is say "paused"
                         about an organisation that is running: `paused_at` is
                         null exactly when it is, enforced by a CHECK on the
                         row rather than by whoever last wrote it. */
                      `Paused ${
                        switched.paused_at === null ? '' : relativeTime(switched.paused_at, now)
                      }${switched.changed_by === user.id ? ' by you' : ''}. Nothing is running.`
                    : switched.changed_at === null
                      ? 'Automation is running.'
                      : `Automation is running. Resumed ${relativeTime(switched.changed_at, now)}${
                          switched.changed_by === user.id ? ' by you' : ''
                        }.`}
                </p>
              ) : null}
            </div>
            <Button
              /* `ghost` while running is the mockup's own tone, and `primary`
                 for *Resume* is the one departure: with automation off, putting
                 it back is the affirmative action on the panel. */
              tone={paused ? 'primary' : 'ghost'}
              busy={move.pending}
              busyLabel={paused ? 'Resuming' : 'Pausing'}
              /* Disabled until the state is known, so the first press cannot
                 send a resume to an organisation that turns out to be running
                 — which the server answers 409 and the tenant reads as a bug. */
              disabled={!mayPause || switched === null}
              title={mayPause ? undefined : 'Only an owner or an admin can pause automation'}
              onClick={() => {
                void (async () => {
                  const next = !paused
                  const result = await move.run(next)
                  if (!result.ok) return
                  automation.refetch()
                  toast.show({
                    kind: 'success',
                    title: next ? 'Automation paused' : 'Automation resumed',
                    body: next
                      ? 'Nothing will run until you turn it back on. Your data and rankings stay.'
                      : 'Scheduled runs and outreach start again on their own schedule.',
                  })
                })()
              }}
            >
              {paused ? 'Resume' : 'Pause'}
            </Button>
          </div>

          <hr className="app-danger__rule" />

          <div className="app-danger__row">
            <div>
              <strong>Export everything (DPDP)</strong>
              <p className="ar-muted app-danger__note">
                A machine-readable copy of every record we hold about your organisation and its
                leads. You will be emailed when it is ready
                {keptDays === null
                  ? ''
                  : `, and it stays downloadable for ${String(keptDays)} days`}
                .
              </p>
            </div>
            <Button
              busy={request.pending}
              busyLabel="Requesting"
              disabled={inFlight}
              onClick={() => {
                void (async () => {
                  const result = await request.run(undefined)
                  if (!result.ok) return
                  exports.refetch()
                  toast.show({
                    kind: 'success',
                    title: 'Export requested',
                    body: 'We will email you when the archive is ready.',
                  })
                })()
              }}
            >
              {inFlight ? 'Being prepared' : 'Request export'}
            </Button>
          </div>
        </div>

        <InlineError
          failure={move.state.failure ?? request.state.failure ?? download.state.failure}
        />

        {exports.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading your exports" lines={2} />
          </div>
        ) : null}

        {exports.state.status === 'error' ? (
          <ErrorState
            failure={exports.state.failure}
            title="Could not load your exports"
            onRetry={exports.refetch}
          />
        ) : null}

        {exports.state.status === 'ready' && rows.length === 0 ? (
          <Empty
            title="No exports yet"
            body="Ask for one above. It is built in the background and emailed to you."
          />
        ) : null}

        {rows.length > 0 ? (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>State</th>
                  <th>Size</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="ar-muted">{relativeTime(row.created_at, now)}</td>
                    <td>
                      <span className={`ar-badge ar-badge--${badgeTone(row.state)}`}>
                        {stateLabel(row.state)}
                      </span>
                      {row.error === null || row.error === undefined ? null : (
                        <div className="ar-subtle app-danger__caption">{row.error}</div>
                      )}
                    </td>
                    <td className="ar-muted ar-num">{formatBytes(row.byte_size ?? null)}</td>
                    <td className="ar-num">
                      {row.downloadable ? (
                        <Button
                          tone="quiet"
                          size="sm"
                          disabled={download.pending}
                          onClick={() => {
                            void (async () => {
                              const result = await download.run(row.id)
                              if (!result.ok) return
                              // A short-lived signed URL, minted on this press
                              // and never stored — so it is followed at once
                              // rather than rendered as a link somebody might
                              // come back to after it has expired.
                              window.location.assign(result.data.url)
                            })()
                          }}
                        >
                          Download
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="ar-card app-danger">
        <div className="ar-card__header">
          <h3 style={{ color: 'var(--ar-danger)' }}>Danger zone</h3>
        </div>
        <div className="ar-card__body ar-stack">
          <div className="app-danger__row">
            <div>
              <strong style={{ color: 'var(--ar-danger)' }}>Delete this organisation</strong>
              <p className="ar-muted app-danger__note">
                Removes your tenant schema, all content, leads, and history. Published pages stay on
                your own site. <strong>This cannot be undone.</strong> We keep billing records for 8
                years as Indian law requires.
              </p>
            </div>
            <Button
              tone="danger"
              onClick={() => {
                setConfirmation('')
                setDeleting(true)
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </section>

      {deleting ? (
        <Modal
          title={`Delete ${tenant.name}?`}
          onClose={() => {
            setDeleting(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setDeleting(false)
                }}
              >
                Keep my account
              </Button>
              <Button
                tone="danger"
                busy={erase.pending}
                busyLabel="Deleting"
                disabled={confirmation.trim() !== tenant.name}
                onClick={() => {
                  void (async () => {
                    const result = await erase.run(confirmation.trim())
                    if (!result.ok) return
                    setDeleting(false)
                    // Signed out rather than routed onward: the session was
                    // scoped to an organisation that no longer exists, and the
                    // server revoked it as the first step of the erasure.
                    await signOut()
                  })()
                }}
              >
                Delete permanently
              </Button>
            </>
          }
        >
          <InlineError failure={erase.state.failure} />
          <p style={{ marginTop: 0 }}>
            This deletes your tenant schema and everything in it — every keyword, article, lead and
            reasoning trail. Anybody else in {tenant.name} loses access immediately.
          </p>
          <p className="ar-muted">
            If you want a copy first, close this and request an export. It cannot be produced after
            the organisation is gone.
          </p>
          <Field label={`Type ${tenant.name} to confirm`}>
            {(props) => (
              <input
                {...props}
                className="ar-input"
                autoComplete="off"
                value={confirmation}
                placeholder={tenant.name}
                onChange={(event) => {
                  setConfirmation(event.target.value)
                }}
              />
            )}
          </Field>
        </Modal>
      ) : null}
    </>
  )
}

function stateLabel(state: string): string {
  // "expired" and "failed" are different answers and the screen says which: one
  // means the archive aged out, the other that it was never built.
  const labels: Record<string, string> = {
    pending: 'Queued',
    running: 'Building',
    ready: 'Ready',
    failed: 'Failed',
    expired: 'Expired',
  }
  return labels[state] ?? state
}

function badgeTone(state: string): string {
  if (state === 'ready') return 'success'
  if (state === 'failed') return 'danger'
  if (state === 'expired') return 'neutral'
  return 'info'
}

function formatBytes(size: number | null): string {
  if (size === null) return '—'
  if (size < 1000) return `${String(size)} B`
  if (size < 1_000_000) return `${(size / 1000).toFixed(1)} KB`
  return `${(size / 1_000_000).toFixed(1)} MB`
}
